"use client"

import { useState, useEffect, useRef } from "react"
import { LightbulbIcon, BugIcon, HelpCircleIcon, XIcon } from "lucide-react"
import { useAuth0 } from '@auth0/auth0-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function NavItem({ label, icon, onClick, isActive }) {
    return (
        <button
            onClick={onClick}
            className={`w-full flex items-center justify-start gap-3 p-3 text-slate-300 hover:text-white hover:bg-slate-800/50 ${isActive ? "bg-slate-800/50 text-white" : ""}`}
        >
            {icon}
            <span className="text-sm">{label}</span>
        </button>
    );
}

export default function FeedbackModal() {
  const [isOpen, setIsOpen] = useState(false)
  const [feedbackType, setFeedbackType] = useState(null)
  const [message, setMessage] = useState("")
  const { user, isAuthenticated } = useAuth0();
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") setIsOpen(false)
    }
    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [])

  const handleFeedbackSubmit = async () => {
      if (!message.trim()) {
        alert("Please enter your feedback before submitting.");
        return;
      }

      try {
        const email = user.email
        console.log('the email', email)
        const response = await fetch("https://trendhopper-c3934b7293e6.herokuapp.com/send_feedback_email/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_email: email,  // Get email from Auth0
            feedback: feedbackType + '_______ ' + message.trim(),
          }),
        });
        console.log('response ok', response.ok)
        if (!response.ok) throw new Error("Failed to submit feedback");

        toast("✅Feedback submitted successfully!");
        setIsOpen(false)
        setFeedbackType(null)
        setMessage("")
      } catch (error) {
        console.error("Error submitting feedback:", error);
        alert("Something went wrong. Please try again.");
      }
    };


  // Handle click outside
  const modalRef = useRef(null)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleSubmit = () => {
    console.log("Feedback Submitted:", { type: feedbackType, message })

    handleFeedbackSubmit()
  }

  const closeFeedbackModal = () => {
    setShowFeedbackModal(false);
    setFeedbackType('');  // Clear the selection
    setMessage('');       // Clear the message
  }


  const FeedbackButton = ({ type, icon: Icon, label }) => (
    <button
      onClick={() => setFeedbackType(type)}
      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border transition-all
        ${
          feedbackType === type
            ? "bg-white/20 border-white/40 text-white"
            : "bg-white/10 border-white/20 text-white/80 hover:bg-white/20 hover:border-white/30"
        }`}
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </button>
  )

  return (
    <>
        <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

    <NavItem
        label="Contact"
        icon={<HelpCircleIcon className="h-6 w-6 sm:h-5 sm:w-5" />}
        isActive={false} // Contact isn't a page, so it won't be highlighted
        onClick={() => setIsOpen(true)}
    />

      {/* Backdrop */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50">
          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div
              ref={modalRef}
              className="relative w-full max-w-md rounded-xl border border-white/20 bg-black/40 backdrop-blur-xl p-6 shadow-lg duration-200 animate-in fade-in slide-in-from-bottom-4"
            >
              {/* Close button */}
              <button
                onClick={() => setIsOpen(false)}
                className="absolute right-4 top-4 rounded-full p-1 text-white/70 hover:text-white/90 hover:bg-white/10 transition-colors"
              >
                <XIcon className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </button>

              {/* Header */}
              <div className="space-y-2">
                <h2 className="text-lg font-semibold tracking-tight text-white">Send Feedback</h2>
                <p className="text-sm text-white/70">
                  Your feedback helps us improve. We'll review it promptly and get in touch with you via email as soon
                  as possible.
                </p>
              </div>

              {/* Content */}
              <div className="mt-6 space-y-4">
                <div className="flex gap-2">
                  <FeedbackButton type="feature" icon={LightbulbIcon} label="Feature" />
                  <FeedbackButton type="bug" icon={BugIcon} label="Bug" />
                  <FeedbackButton type="other" icon={HelpCircleIcon} label="Other" />
                </div>

                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Enter your feedback here..."
                  className="w-full min-h-[100px] rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm text-white placeholder:text-white/50
                    focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/30 transition-colors"
                />
              </div>

              {/* Footer */}
              <div className="mt-6 flex justify-end gap-2">
                <button
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 rounded-lg border border-white/20 bg-white/10 text-white/80
                    hover:bg-white/20 hover:border-white/30 transition-all text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!feedbackType || !message.trim()}
                  className="px-4 py-2 rounded-lg bg-white/20 text-white border border-white/30
                    hover:bg-white/30 transition-all text-sm font-medium
                    disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Submit Feedback
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

